<mat-accordion multi="true" class="submenu-accordion display-block w-full">
  <mat-expansion-panel 
    *ngFor="let s of submenus; trackBy: trackById" 
    (opened)="onOpened(s.id)"
    class="mb-2 border rounded-lg overflow-hidden">
    
    <mat-expansion-panel-header class="h-16 bg-slate-50">
      <mat-panel-title class="text-base font-bold text-slate-700 flex items-center gap-2">
        <mat-icon class="text-slate-400">analytics</mat-icon>
        {{ s.title }}
      </mat-panel-title>
    </mat-expansion-panel-header>

    <div class="flex flex-col items-center justify-center py-12 text-slate-500" *ngIf="loading()[s.id]">
      <mat-spinner diameter="40" color="primary"></mat-spinner>
      <span class="mt-4 text-sm font-medium animate-pulse">Gathering task statistics...</span>
    </div>

    <div class="flex flex-col items-center justify-center py-12 text-red-600 bg-red-50 m-4 rounded-lg" *ngIf="error()[s.id]">
      <mat-icon class="text-4xl mb-2 h-10 w-10">error_outline</mat-icon>
      <span class="font-semibold">Unable to load data</span>
      <span class="text-sm opacity-80">{{ error()[s.id] }}</span>
    </div>

    <ng-container *ngIf="!loading()[s.id] && loaded()[s.id]">
      
      <ng-container *ngIf="data()[s.id] as res">

        <div class="p-4 sm:p-6 bg-slate-50">
          
          <div class="flex items-center justify-between mb-6">
            <h4 class="text-lg font-bold text-slate-800 border-l-4 border-green-600 pl-3">
              Performance Overview
            </h4>
            <span class="text-xs text-slate-400 bg-white px-2 py-1 rounded border">
              Live Data
            </span>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            
            <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col h-full">
              <div class="text-center mb-4">
                <h5 class="font-semibold text-slate-700">Last 3 Months</h5>
                <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  Total: {{ l3Total(res) }}
                </span>
              </div>

              <div class="flex-1 relative min-h-[250px] flex items-center justify-center">
                <ng-container *ngIf="l3Total(res) > 0; else noData">
                  <canvas baseChart 
                          [type]="'pie'" 
                          [data]="buildL3MonthsPie(res)" 
                          [options]="pieOptions">
                  </canvas>
                </ng-container>
              </div>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col h-full">
              <div class="text-center mb-4">
                <h5 class="font-semibold text-slate-700">Yesterday</h5>
                <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  Total: {{ yTotal(res) }}
                </span>
              </div>

              <div class="flex-1 relative min-h-[250px] flex items-center justify-center">
                <ng-container *ngIf="yTotal(res) > 0; else noData">
                  <canvas baseChart 
                          [type]="'pie'" 
                          [data]="buildYesterdayPie(res)" 
                          [options]="pieOptions">
                  </canvas>
                </ng-container>
              </div>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col h-full lg:col-span-2 xl:col-span-1">
              <div class="text-center mb-4">
                <h5 class="font-semibold text-slate-700">Avg. Duration</h5>
                <span class="text-xs text-slate-400">Target: &lt; {{slaDays}} days</span>
              </div>
              
              <div class="flex-1 flex items-center justify-center py-4">
                <div class="relative w-[200px] h-[200px]">
                  <mat-progress-spinner 
                    class="absolute inset-0 opacity-20"
                    mode="determinate" 
                    [value]="100" 
                    strokeWidth="12" 
                    diameter="200" 
                    color="primary">
                  </mat-progress-spinner>
                  
                  <mat-progress-spinner 
                    class="absolute inset-0 z-10"
                    mode="determinate" 
                    [value]="gaugeValue(res)" 
                    strokeWidth="12" 
                    diameter="200" 
                    [color]="gaugeValue(res) > 100 ? 'warn' : 'primary'">
                  </mat-progress-spinner>

                  <div class="absolute inset-0 flex flex-col items-center justify-center select-none">
                    <span class="text-4xl font-bold text-slate-800">{{ overallAvgDays(res) }}</span>
                    <span class="text-sm font-medium text-slate-500 uppercase tracking-wide">Days</span>
                    <span class="text-xs text-slate-400 mt-1 bg-slate-100 px-2 rounded">
                      {{ formatSeconds(res?.tasksStatsAvgDurationInSeconds) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mt-6">
            <div class="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <h5 class="text-sm font-bold text-slate-600 uppercase tracking-wider">Data Breakdown</h5>
            </div>
            
            <div class="overflow-x-auto">
              <table class="w-full text-sm text-left">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th colspan="3" class="py-3 px-4 bg-green-50 text-green-800 font-bold uppercase border-r border-slate-100 text-center">
                      Last 3 Months
                    </th>
                    <th colspan="3" class="py-3 px-4 bg-rose-50 text-rose-800 font-bold uppercase text-center">
                      Yesterday
                    </th>
                  </tr>
                  
                  <tr class="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase font-semibold">
                    <th class="py-3 px-4 w-1/6 text-center">Completed</th>
                    <th class="py-3 px-4 w-1/6 text-center">Pending</th>
                    <th class="py-3 px-4 w-1/6 border-r border-slate-200 text-center">Total</th>
                    <th class="py-3 px-4 w-1/6 text-center">Completed</th>
                    <th class="py-3 px-4 w-1/6 text-center">Pending</th>
                    <th class="py-3 px-4 w-1/6 text-center">Total</th>
                  </tr>
                </thead>

                <tbody>
                  <tr class="hover:bg-slate-50 transition-colors">
                    <td class="py-4 px-4 font-medium text-green-600 text-center">
                      {{ res.lastThreeMonthsCompletedTasks }}
                    </td>
                    <td class="py-4 px-4 text-slate-600 text-center">
                      {{ res.lastThreeMonthsNotCompletedTasks }}
                    </td>
                    <td class="py-4 px-4 font-bold text-slate-800 border-r border-slate-100 bg-slate-50/50 text-center">
                      {{ l3Total(res) }}
                    </td>

                    <td class="py-4 px-4 font-medium text-green-600 text-center">
                      {{ res.fromYesterdayCompletedTasks }}
                    </td>
                    <td class="py-4 px-4 text-slate-600 text-center">
                      {{ res.fromYesterdayNotCompletedTasks }}
                    </td>
                    <td class="py-4 px-4 font-bold text-slate-800 bg-slate-50/50 text-center">
                      {{ yTotal(res) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

      </ng-container> 
    </ng-container>
    
    <ng-template #noData>
      <div class="flex flex-col items-center justify-center h-full w-full text-slate-400 p-6">
        <mat-icon class="text-4xl text-slate-200 mb-2">inbox</mat-icon>
        <span class="text-sm">No tasks found</span>
      </div>
    </ng-template>

  </mat-expansion-panel>
</mat-accordion>